import { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import ProductCarousel from "./ProductCarousel";
import ProductReview from "./productReview";

const ProductView = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || "";

  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pincode, setPincode] = useState("");
  const [getwishlist, setGetWishlist] = useState([]);
  const [deliveryMsg, setDeliveryMsg] = useState("");
  const [isAvailable, setIsAvailable] = useState(false);

  // New states for variants
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [displayPrice, setDisplayPrice] = useState(0);

 // Check delivery availability
const checkPincode = () => {
  if (!pincode) {
    setDeliveryMsg("⚠️ Please enter a pincode");
    setIsAvailable(false);
    return;
  }

  const availablePins = ["110001", "560001", "400001", "700001", "600001","110090","110094"]; // Example pincodes
  if (availablePins.includes(pincode)) {
    setDeliveryMsg("✅ Delivery available at your location!");
    setIsAvailable(true);
  } else {
    setDeliveryMsg("❌ Sorry, delivery not available at this location.");
    setIsAvailable(false);
  }
};

// Fetch product details
useEffect(() => {
  const fetchProduct = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`https://cartbackend.wsdstech.com/api/products/${id}`);
      let productData = res.data;

      // Parse variants if string
      if (typeof productData.variants === "string") {
        try { productData.variants = JSON.parse(productData.variants); } 
        catch { productData.variants = []; }
      }

      // Parse variant_prices if string
      if (typeof productData.variant_prices === "string") {
        try { productData.variant_prices = JSON.parse(productData.variant_prices); } 
        catch { productData.variant_prices = {}; }
      }

      setProduct(productData);

      // Default variant
      if (Array.isArray(productData.variants) && productData.variants.length > 0) {
        setSelectedVariant(productData.variants[0]);
      } else {
        setSelectedVariant(null);
      }

    } catch (err) {
      console.error("Error fetching product:", err);
    } finally {
      setLoading(false);
    }
  };

  fetchProduct();
}, [id]);

// Update display price whenever variant changes
useEffect(() => {
  if (!product) return;

  if (!selectedVariant) {
    setDisplayPrice(product.price);
    return;
  }

  if (typeof selectedVariant === "object") {
    setDisplayPrice(selectedVariant.price || product.price);
  } else {
    setDisplayPrice(product.variant_prices?.[selectedVariant] || product.price);
  }
}, [selectedVariant, product]);

// Add to cart
const handleAddToCart = () => {
  if (!product) return;

  const variantName = selectedVariant?.name || selectedVariant || "";

  if (!user) {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    const existingItemIndex = cart.findIndex(
      (item) => item.product_id === product.id && item.variant === variantName
    );

    if (existingItemIndex >= 0) {
      cart[existingItemIndex].quantity += quantity;
    } else {
      cart.push({
        product_id: product.id,
        name: product.name,
        price: displayPrice, // Use displayPrice here
        quantity,
        variant: variantName,
        image: product.image_url || product.image,
      });
    }

    localStorage.setItem("cart", JSON.stringify(cart));
    alert("✅ Added to cart successfully (LocalStorage)!");
    window.location.reload();
    return;
  }

  // For logged-in users
  axios
    .post(
      "https://cartbackend.wsdstech.com/api/cart",
      {
        product_id: product.id,
        quantity,
        variant: variantName,
        price: displayPrice, // send variant price to backend if needed
      },
      { headers: { Authorization: `Bearer ${token}` } }
    )
    .then(() => alert("✅ Added to cart successfully!"))
    .then(() => window.location.reload())
    .catch((err) =>
      alert("❌ Error: " + (err.response?.data?.message || "Something went wrong"))
    );
};


// Fetch wishlist
useEffect(() => {
  if (!token) return;

  axios.get("https://cartbackend.wsdstech.com/api/wishlist", {
    headers: { Authorization: `Bearer ${token}` }
  })
  .then(res => {
    const ids = res.data.map(item => item.product_id);
    setGetWishlist(ids);
  })
  .catch(err => console.error("Wishlist fetch error:", err));
}, [token]);

// Toggle wishlist
const handleToggleWishlist = async (productId) => {
  if (!token) {
    let guestWishlist = JSON.parse(localStorage.getItem("guestWishlist")) || [];
    guestWishlist.includes(productId)
      ? guestWishlist = guestWishlist.filter(id => id !== productId)
      : guestWishlist.push(productId);
    localStorage.setItem("guestWishlist", JSON.stringify(guestWishlist));
    setGetWishlist(guestWishlist);
    return;
  }

  try {
    await axios.post(
      "https://cartbackend.wsdstech.com/api/wishlist",
      { product_id: productId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setGetWishlist(prev => [...prev, productId]);
  } catch (error) {
    console.error("Error toggling wishlist:", error);
  }
};

// Render states
if (loading) return <p className="text-center my-5">⏳ Loading product...</p>;
if (!product) return <p className="text-center my-5 text-danger">⚠️ Product not found</p>;

// console.log("Selected Variant:", selectedVariant);
  return (
    <div className="container my-5">
      <div className="row g-4">
        {/* Product Image */}
        <div className="col-md-6">
          <div
            className="card shadow-sm p-3 position-sticky"
            style={{ top: "100px" }}
          >
            <img
              src={product.image_url}
              alt={product.name}
              className="img-fluid rounded"
              style={{ maxHeight: "500px", objectFit: "contain" }}
            />
          </div>
        </div>

        {/* Product Details */}
        <div className="col-md-6">
          <div className="card border-0">
            <div className="card-body">
              <h2 className="fw-bold">{product.name}</h2>

              {product.brand && (
                <p className="text-muted">
                  Brand: <strong>{product.brand}</strong>
                </p>
              )}

              <h4 className="text-dark mb-3">₹{displayPrice}</h4>
          <p
  className="text-muted"
  dangerouslySetInnerHTML={{ __html: product.description }}
></p>

             {/* Variants */}
{product.variants?.length > 0 && (
  <div className="mb-3">
    <label className="fw-bold">Choose Variant:</label>
    <select
      className="form-select mt-2"
      value={selectedVariant?.name || ""}
      onChange={(e) => {
        const val = e.target.value;

        if (typeof product.variants[0] === "object") {
          // Find the variant object by name
          const found = product.variants.find((v) => v.name === val);
          setSelectedVariant(found || null);
        } else {
          setSelectedVariant(val);
        }
      }}
    >
      <option value="" disabled>
        -- Select Variant --
      </option>
      {product.variants.map((variant, index) => {
        if (typeof variant === "object") {
          return (
            <option key={index} value={variant.name}>
              {variant.name} (₹{variant.price})
            </option>
          );
        }
        return (
          <option key={index} value={variant}>
            {variant} (₹{product.variant_prices?.[variant] || product.price})
          </option>
        );
      })}
    </select>
  </div>
)}




              {/* Quantity */}
              <div className="d-flex align-items-center mb-3">
                <button
                  className="btn btn-outline-secondary"
                  disabled={quantity <= 1}
                  onClick={() => setQuantity((prev) => prev - 1)}
                >
                  -
                </button>
                <input
                  type="text"
                  value={quantity}
                  readOnly
                  className="form-control text-center mx-2"
                  style={{ width: "60px" }}
                />
                <button
                  className="btn btn-outline-secondary"
                  disabled={quantity >= product.stock}
                  onClick={() => setQuantity((prev) => prev + 1)}
                >
                  +
                </button>
              </div>

              <button
                className="btn btn-dark btn-lg w-100 mb-4"
                onClick={handleAddToCart}
                disabled={product.stock === 0 || !isAvailable}
              >
                🛒 Add {quantity} to Cart
              </button>

              <button
                className={`btn ${
                  getwishlist.includes(product.id)
                    ? "btn-danger"
                    : "btn-outline-danger"
                } w-100`}
                onClick={() => handleToggleWishlist(product.id)}
                disabled={product.stock === 0 || !isAvailable}
              >
                {getwishlist.includes(product.id)
                  ? "Remove from Wishlist"
                  : "Add to Wishlist"}
              </button>

              {/* Delivery Check */}
              <div className="mb-4 mt-4">
                <label className="form-label fw-bold">
                  Check Delivery Availability
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Pincode"
                    maxLength={6}
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value)}
                  />
                  <button
                    className="btn btn-outline-dark"
                    type="button"
                    onClick={checkPincode}
                  >
                    Check
                  </button>
                </div>
                {deliveryMsg && (
                  <p
                    className={`mt-2 ${
                      isAvailable ? "text-success" : "text-dark"
                    }`}
                  >
                    {deliveryMsg}
                  </p>
                )}
              </div>
              {product.more_description && (
  <div className="mt-4">
    <h4>More Description</h4>
    <p dangerouslySetInnerHTML={{ __html: product.more_description }}></p>
  </div>
)}

{/* Other Details */}
{product.other_details && (
  <div className="mt-4">
    <h4>Other Details</h4>
    <p dangerouslySetInnerHTML={{ __html: product.other_details }}></p>
  </div>
)}

{/* FAQs Accordion */}
{product.faqs?.length > 0 && (
  <div className="mt-4">
    <h4>FAQs</h4>
    <div className="accordion" id="faqAccordion">
      {product.faqs.map((faq, index) => (
        <div className="accordion-item" key={index}>
          <h2 className="accordion-header" id={`heading${index}`}>
            <button
              className="accordion-button collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target={`#collapse${index}`}
              aria-expanded="false"
              aria-controls={`collapse${index}`}
            >
              {faq.question}
            </button>
          </h2>
          <div
            id={`collapse${index}`}
            className="accordion-collapse collapse"
            aria-labelledby={`heading${index}`}
            data-bs-parent="#faqAccordion"
          >
            <div
              className="accordion-body"
              dangerouslySetInnerHTML={{ __html: faq.answer }}
            ></div>
          </div>
        </div>
      ))}
    </div>
  </div>
)}

            </div>
          </div>
        </div>
      </div>

      <ProductCarousel />
      <ProductReview productId={product.id} user={user} />
    </div>
  );
};

export default ProductView;
